import { useState, useEffect } from "react";
import { sendSelectedContactsEmail, fetchContacts } from "../../../api/contacts";
import toast from "react-hot-toast";
import { FiSend, FiCheckSquare, FiSquare, FiSearch } from "react-icons/fi";

export default function SelectedContactsSend({ token, templateId, setResult, initialSelectedIds = [], trackOpens = false, includeUnsubscribe = false }) {
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedIds));
  const [loading, setLoading] = useState(false);
  const [fetchingContacts, setFetchingContacts] = useState(true);
  const [search, setSearch] = useState("");

  // Load contacts for selection list
  useEffect(() => {
    if (!token?.access) return;
    setFetchingContacts(true);
    fetchContacts(token.access, 1, 100, search)
      .then((data) => {
        setContacts(data.results || []);
      })
      .catch((err) => console.error("Failed to load contacts for selection:", err))
      .finally(() => setFetchingContacts(false));
  }, [token, search]);

  const toggleSelect = (id) => {
    const updated = new Set(selectedIds);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    setSelectedIds(updated);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === contacts.length && contacts.length > 0) {
      setSelectedIds(new Set());
    } else {
      const allIds = new Set(contacts.map((c) => c.id));
      setSelectedIds(allIds);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!templateId) {
      toast.error("Please select an Email Template above first!");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Please select at least one contact to send email.");
      return;
    }

    setLoading(true);
    try {
      const res = await sendSelectedContactsEmail(
        token.access,
        Array.from(selectedIds),
        templateId,
        trackOpens,
        includeUnsubscribe
      );
      setResult(res);
      toast.success(`Successfully sent emails to ${res.sent_count} selected contacts!`);
    } catch (err) {
      toast.error(err.message || "Failed to send emails");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSend} className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FiCheckSquare className="text-blue-600" /> Select Specific Contacts to Email
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Choose exact contacts from your list regardless of whether they received emails before.
          </p>
        </div>

        {/* Selected Count Badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
            {selectedIds.size} Selected
          </span>
          {contacts.length > 0 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-gray-600 hover:text-blue-600 underline font-medium"
            >
              {selectedIds.size === contacts.length ? "Deselect All" : "Select All on Page"}
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts by name or email..."
          className="w-full pl-9 pr-4 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      </div>

      {/* Contacts Checkbox Selector List */}
      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
        {fetchingContacts ? (
          <p className="p-4 text-xs text-gray-500 text-center">Loading contacts...</p>
        ) : contacts.length === 0 ? (
          <p className="p-4 text-xs text-gray-400 text-center">No matching contacts found.</p>
        ) : (
          contacts.map((contact) => {
            const isChecked = selectedIds.has(contact.id);
            return (
              <label
                key={contact.id}
                onClick={() => toggleSelect(contact.id)}
                className={`flex items-center justify-between p-3 cursor-pointer hover:bg-blue-50/50 transition text-xs ${
                  isChecked ? "bg-blue-50/70" : ""
                }`}
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="text-blue-600 shrink-0">
                    {isChecked ? <FiCheckSquare size={16} /> : <FiSquare size={16} className="text-gray-300" />}
                  </div>
                  <div className="truncate">
                    <span className="font-semibold text-gray-900">{contact.name}</span>
                    {contact.company_name && (
                      <span className="text-gray-500 ml-1.5">({contact.company_name})</span>
                    )}
                    <div className="text-gray-500 font-mono text-[11px] truncate">{contact.email}</div>
                  </div>
                </div>

                <div className="shrink-0 ml-2">
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border text-gray-600 font-medium">
                    {contact.category || "General"}
                  </span>
                </div>
              </label>
            );
          })
        )}
      </div>

      <button
        type="submit"
        disabled={loading || selectedIds.size === 0}
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2.5 rounded-lg text-sm flex items-center gap-2 transition disabled:opacity-50 shadow-sm"
      >
        <FiSend size={16} />
        {loading
          ? "Sending Emails..."
          : `Send Email to ${selectedIds.size} Selected Contact${selectedIds.size === 1 ? "" : "s"}`}
      </button>
    </form>
  );
}
